export const LabRisk = {
  LOW: "LOW",
  CONTROLLED: "CONTROLLED",
} as const;

export interface PracticalLab {
  readonly id: string;
  readonly title: string;
  readonly outcome: string;
  readonly risk: (typeof LabRisk)[keyof typeof LabRisk];
  readonly prerequisites: readonly string[];
  readonly steps: readonly string[];
  readonly evidence: readonly string[];
  readonly cleanup: readonly string[];
}

export const PRACTICAL_LABS = [
  {
    id: "request-path",
    title: "Trace a proxied request",
    outcome: "Prove the DNS, TLS, edge, and origin boundaries for a test hostname.",
    risk: LabRisk.LOW,
    prerequisites: ["A Cloudflare account", "A test domain or disposable test hostname", "Browser developer tools and curl"],
    steps: [
      "Document the authoritative nameservers and the hostname's DNS record.",
      "Confirm proxy status and resolve the hostname from a terminal.",
      "Inspect response headers and the browser's certificate details.",
      "Capture one successful request in Cloudflare HTTP analytics.",
      "Draw the two TLS connections and label which certificate each side validates.",
    ],
    evidence: ["DNS result", "Certificate subject and issuer", "Response headers", "Cloudflare analytics record", "Architecture drawing"],
    cleanup: ["Keep the test hostname isolated from production", "Remove any temporary diagnostic DNS record no longer required"],
  },
  {
    id: "waf-baseline",
    title: "Deploy and observe WAF protections",
    outcome: "Enable baseline managed protection and distinguish traffic analytics from security events.",
    risk: LabRisk.CONTROLLED,
    prerequisites: ["A non-production proxied hostname", "Permission to view and change zone security settings"],
    steps: [
      "Record the current managed-rules configuration before changing it.",
      "Enable the ruleset available to the test plan and leave existing production zones untouched.",
      "Send ordinary test requests using several methods and paths.",
      "Compare Security Analytics traffic with Security Events.",
      "Identify the documented phase in which Managed Rules execute.",
    ],
    evidence: ["Before-and-after configuration", "Traffic filters", "Event details if any", "Written phase order"],
    cleanup: ["Retain the secure baseline on the isolated test hostname", "Restore any setting that was changed only for observation"],
  },
  {
    id: "custom-rule",
    title: "Create a narrow custom rule",
    outcome: "Build, test, and explain a path-specific Managed Challenge without affecting unrelated traffic.",
    risk: LabRisk.CONTROLLED,
    prerequisites: ["A non-production hostname", "A unique test path", "WAF custom-rule permission"],
    steps: [
      "Define the exact method, hostname, and unique path that identify only your test request.",
      "Create a custom rule with Managed Challenge or Log if the account exposes it.",
      "Test a matching request and a non-matching control request.",
      "Inspect the resulting event and record the rule identifier.",
      "Move the rule within the custom phase and explain how order changes behavior.",
    ],
    evidence: ["Expression", "Rule action", "Matching event", "Non-matching control result", "Ordering explanation"],
    cleanup: ["Disable or delete the isolated test rule after collecting evidence"],
  },
  {
    id: "false-positive",
    title: "Resolve a simulated false positive",
    outcome: "Use evidence to design the narrowest exception rather than disabling protection broadly.",
    risk: LabRisk.CONTROLLED,
    prerequisites: ["A non-production rule that intentionally blocks a known safe test request", "Security Events access"],
    steps: [
      "Generate a known safe request that the test rule blocks.",
      "Filter Security Events and capture service, action, rule ID, path, method, and source context.",
      "Write the smallest condition that distinguishes the known safe request.",
      "Create the scoped exception before the rule it must bypass.",
      "Retest the safe request and a malicious-control pattern that must remain blocked.",
    ],
    evidence: ["Original block event", "Exception expression", "Allowed safe retest", "Blocked control retest"],
    cleanup: ["Remove the simulation rules and exceptions", "Confirm the original secure baseline remains enabled"],
  },
  {
    id: "zero-trust-org",
    title: "Create the Zero Trust identity boundary",
    outcome: "Establish a test organization, identity login, and narrowly scoped enrollment policy.",
    risk: LabRisk.LOW,
    prerequisites: ["A Cloudflare account", "A test identity provider or a deliberately constrained OTP population"],
    steps: [
      "Create the Zero Trust organization and record its team domain.",
      "Connect the test identity provider and verify the expected identity appears.",
      "Create enrollment permissions restricted to the intended test identity population.",
      "Enroll one test device and confirm its identity in the device inventory.",
      "Explain why device posture was not used as the first enrollment control.",
    ],
    evidence: ["Team domain", "IdP integration state", "Enrollment policy", "Enrolled device record"],
    cleanup: ["Remove test identities or devices that should not remain enrolled", "Preserve the narrow enrollment boundary"],
  },
  {
    id: "tunnel-access",
    title: "Publish a private app through Tunnel and Access",
    outcome: "Reach a private HTTP application without opening an inbound port and enforce identity-aware access.",
    risk: LabRisk.CONTROLLED,
    prerequisites: ["A harmless local test web service", "cloudflared", "A test hostname", "An enrolled identity"],
    steps: [
      "Run the test service on a loopback or isolated development interface.",
      "Create a named Tunnel and add the connector using the dashboard-provided command.",
      "Map a test public hostname to the local service without opening an inbound firewall port.",
      "Create an Access self-hosted application and an Allow policy for only the test identity.",
      "Test authorized, unauthenticated, and unauthorized flows and inspect Access logs.",
    ],
    evidence: ["Healthy connector", "Hostname mapping", "Access policy", "Three test outcomes", "Access log entries"],
    cleanup: ["Delete the temporary hostname and Tunnel if they are not part of continued study", "Stop the local test service"],
  },
  {
    id: "gateway",
    title: "Build Gateway DNS and HTTP policy",
    outcome: "Observe policy precedence and understand when TLS inspection is required.",
    risk: LabRisk.CONTROLLED,
    prerequisites: ["An enrolled test device", "Cloudflare One Client connected to the test organization", "A trusted test root certificate for HTTPS inspection if used"],
    steps: [
      "Create a narrowly scoped DNS policy for a harmless test destination or category.",
      "Confirm the DNS action in Gateway logs.",
      "Create a more specific exception and place it above a broader rule.",
      "Enable HTTPS inspection only on the test profile after installing the intended root certificate.",
      "Create and test an HTTP rule, then explain DNS → network → HTTP evaluation.",
    ],
    evidence: ["DNS log", "Policy order", "Root trust state", "HTTP log", "Enforcement-order explanation"],
    cleanup: ["Remove temporary rules", "Disable test-only TLS inspection if the profile will be reused outside the lab"],
  },
  {
    id: "troubleshooting",
    title: "Run a boundary-based troubleshooting drill",
    outcome: "Diagnose a broken private application without weakening unrelated controls.",
    risk: LabRisk.CONTROLLED,
    prerequisites: ["Completed Tunnel and Access lab", "An enrolled test device", "Permission to inspect client, Access, and Gateway logs"],
    steps: [
      "Create one deliberate failure: wrong Access group, missing route, private DNS mismatch, stale profile, or stopped connector.",
      "Start at the device and record client connection, identity, applied profile, route, and DNS state.",
      "Inspect Access or Gateway logs for the policy decision.",
      "Inspect connector health and the local service only after upstream boundaries pass.",
      "Correct the source misconfiguration and demonstrate recovery using the same evidence path.",
    ],
    evidence: ["Failure symptom", "Boundary checks", "Root cause", "Configuration correction", "Successful retest"],
    cleanup: ["Restore the intended secure state", "Confirm no diagnostic exception remains"],
  },
] as const satisfies readonly PracticalLab[];
